/**
 * `/setup`'s pure pieces (slice 1b spec §6.2): where Connect your AI is,
 * the provider it's about, the headline for each state, and the cards'
 * notes and rows. The components in `setup-page.tsx` and
 * `setup-connect.tsx` only render what these return.
 */
import type { AiProvider, Me } from "../../app/me";
import {
  clockTime,
  connectAccount,
  connectStages,
  isOnboarding,
  PROVIDERS,
  setupSteps,
  signsIn,
  type setupPhase,
  type StartedConnect,
  type StepState,
} from "./onboarding";

type Computer = NonNullable<Me["computer"]>;

type Ai = Computer["ai"];

export type Phase = ReturnType<typeof setupPhase>;

export type ChecklistRow = {
  id: string;
  label: string;
  state: StepState;
  /** A running row that waits its turn: copper, "In line". */
  queued?: boolean;
};

export type RowLook = {
  icon: StepState | "queued";
  says: string;
  tone: string;
};

const LOOKS: Record<StepState, RowLook> = {
  done: { icon: "done", says: "Done", tone: "text-hint" },
  running: { icon: "running", says: "Working", tone: "font-medium text-moss" },
  failed: { icon: "failed", says: "Failed", tone: "font-medium text-destructive" },
  pending: { icon: "pending", says: "", tone: "" },
};

const IN_LINE: RowLook = { icon: "queued", says: "In line", tone: "font-medium text-copper" };

/** How a checklist row looks: its icon, and what it says on the right. */
export function rowLook(row: Pick<ChecklistRow, "state" | "queued">): RowLook {
  return row.queued && row.state === "running" ? IN_LINE : LOOKS[row.state];
}

export type SetupView = "form" | "sign-in" | "connecting" | "failed" | "done";

/** The stages a sign-in's connect shows its sign-in in: until it's approved. */
const SIGNING_IN = new Set<Ai["stage"]>([undefined, "waking", "signing_in"]);

/**
 * What Connect your AI shows: the form; the sign-in until it's
 * approved; the stages while connecting; why a connect failed, until the
 * owner goes back to the form (`editing`); or done.
 */
export function setupView(
  ai: Ai | null,
  state: { done: boolean; editing: boolean; provider: AiProvider | null },
): SetupView {
  if (state.done) return "done";
  if (ai?.status === "connecting") return connectingView(ai, state.provider);
  return ai?.status === "failed" && !state.editing ? "failed" : "form";
}

function connectingView(ai: Ai, provider: AiProvider | null): SetupView {
  if (ai.pending) return "sign-in";
  return signsIn(provider) && SIGNING_IN.has(ai.stage) ? "sign-in" : "connecting";
}

/**
 * The provider a connect under way (or failed) is for: its account's,
 * else the one this page started, else the workspace's. Otherwise the one
 * picked in the form.
 */
export function connectProvider(
  ai: Ai | null,
  started: StartedConnect | undefined,
  chosen: AiProvider | null,
): AiProvider | null {
  if (ai?.status !== "connecting" && ai?.status !== "failed") return chosen;
  return connectAccount(ai)?.provider ?? started?.provider ?? ai.provider ?? chosen;
}

/**
 * The provider the card shows: the one signing in while it does (ChatGPT
 * when nothing says which), the pick in the form, else the connect's.
 */
export function shownProvider(
  view: SetupView,
  chosen: AiProvider | null,
  connecting: AiProvider | null,
): AiProvider | null {
  if (view === "sign-in") return signsIn(connecting) ? connecting : "chatgpt";
  return view === "form" ? chosen : connecting;
}

export type Intro = { eyebrow: string; title: string; lead: string };

const STEP_1 = "Setup · step 1 of 2";
const STEP_2 = "Setup · step 2 of 2";

/** The headline while the computer sets up, waits, or stopped: by phase, then owner or member. */
const COMPUTER_INTROS: Record<Exclude<Phase, "ready">, (owner: boolean) => Intro> = {
  building: () => ({
    eyebrow: STEP_1,
    title: "Building your computer.",
    lead: "Your Chief of Staff lives on a computer of its own. It takes a few minutes to set up. You can close this tab: it keeps going, and picks up here when you come back.",
  }),
  waiting: () => ({
    eyebrow: STEP_1,
    title: "Waiting for a computer.",
    lead: "There isn't a free computer this minute. You're in line for the next one, and nothing is lost while you wait. Setup picks up the moment one frees up.",
  }),
  failed: (owner) => ({
    eyebrow: STEP_1,
    title: "Setup stopped.",
    lead: owner
      ? "Your computer couldn't finish setting up. Nothing you did caused this, and nothing is lost. Retry starts again on a fresh computer."
      : "Your computer couldn't finish setting up. Nothing is lost: the workspace owner can retry.",
  }),
};

type ConnectIntro = { name: string; signIn: boolean; firstName: string; owner: boolean };

/** The headline once the computer is ready: by where Connect your AI is. */
const CONNECT_INTROS: Record<SetupView, (c: ConnectIntro) => Intro> = {
  form: ({ owner }) => ({
    eyebrow: STEP_2,
    title: "Connect your AI.",
    lead: owner
      ? "Your Chief of Staff thinks with an AI account of yours. Pick the one you have: the provider bills you for it, not Crew. You can add more accounts later in Settings."
      : "Your Chief of Staff thinks with an AI account of the workspace's. The workspace owner connects it.",
  }),
  "sign-in": (c) => CONNECT_INTROS.form(c),
  connecting: ({ name, signIn }) => ({
    eyebrow: STEP_2,
    title: `Connecting ${name}.`,
    lead: `${signIn ? "You approved the sign-in." : `${name} took the key.`} Now your computer saves it and restarts your Chief of Staff with it. This takes about a minute.`,
  }),
  failed: ({ name, signIn }) => ({
    eyebrow: STEP_2,
    title: `Couldn't connect ${name}.`,
    lead: signIn
      ? "The sign-in didn't finish on your computer. Try again for a new code."
      : `${name} took the key, but your computer couldn't finish connecting. Try again: for safety, Crew asks you to paste the key once more.`,
  }),
  done: ({ firstName }) => ({
    eyebrow: "Setup · done",
    title: `You're all set, ${firstName}.`,
    lead: "Your Chief of Staff is awake and connected. Ask it for anything: a plan for the week, a quote to chase, a note to your crew.",
  }),
};

/** The headline: where setup is, and once the computer is ready, where the connect is. */
export function setupIntro(
  phase: Phase,
  view: SetupView,
  who: { provider: AiProvider | null; name: string; owner: boolean },
): Intro {
  if (phase !== "ready") return COMPUTER_INTROS[phase](who.owner);
  return CONNECT_INTROS[view]({
    name: who.provider ? PROVIDERS[who.provider].name : "your AI",
    signIn: signsIn(who.provider),
    firstName: who.name.split(/\s+/)[0],
    owner: who.owner,
  });
}

/** The footer under the steps: how long it takes, the line, or that Retry is safe. */
export function stepsNote(phase: Phase, startedAt: string | null, timezone: string): string {
  if (phase === "waiting") {
    return "Your place in line is kept if you close this tab. Setup carries on by itself.";
  }
  if (phase === "failed") {
    return "Retrying is safe. Your workspace, your Chief of Staff and your payment are all kept.";
  }
  const started = startedAt ? ` Started at ${clockTime(startedAt, timezone)}.` : "";
  return `Usually about 4 minutes.${started}`;
}

/** Card 1 while setting up: its status, the clock's start, the rows, and why it stopped. */
export function stepsCard(computer: Me["computer"]) {
  return {
    status: computer?.status ?? "setting_up",
    since: computer?.setup_started_at ?? null,
    rows: stepRows(computer),
    error: computer?.error ?? "Setting up your computer didn't finish.",
  };
}

/** The §3.1 checklist's rows; with no computer yet, the first step runs. */
export function stepRows(computer: Pick<Computer, "status" | "step"> | null): ChecklistRow[] {
  return setupSteps(computer ?? { status: "setting_up", step: null }).map((s) => ({
    id: s.step,
    label: s.label,
    state: s.state,
    queued: s.step === "waiting",
  }));
}

/** The footer under the connect form: who bills for it, or that the sign-in carries on. */
export function formNote(provider: AiProvider | null, signingIn: boolean): string {
  if (signingIn) return "It carries on by itself once you approve. You can leave this tab open.";
  if (!provider) return "Pick the one you already have. You can add more later in Settings.";
  if (signsIn(provider)) return `Your ${PROVIDERS[provider].name} plan covers what your crew uses.`;
  // Workers AI is billed to the owner's Cloudflare account (slice 1h).
  if (provider === "workers-ai") return "Cloudflare bills your account for what your crew uses.";
  return `${PROVIDERS[provider].name} bills you for what your crew uses.`;
}

/** The model the form shows: the one sent while signing in, else the one picked (none: the default). */
export function formPick(
  signingIn: boolean,
  picked: string | undefined,
  sent: string | null | undefined,
): string | undefined {
  return signingIn ? (sent ?? undefined) : picked;
}

/** The form's `data-connect-state`: the form, the sign-in waiting for its code, or the code. */
export function formState(signingIn: boolean, pending: Ai["pending"]): string {
  if (!signingIn) return "form";
  return pending ? "code" : "signing-in";
}

/** "Claude · Claude Sonnet 5.5", or the provider alone before the model is known. */
export function summaryTitle(provider: AiProvider, model: string | null): string {
  const name = PROVIDERS[provider].name;
  return model ? `${name} · ${model}` : name;
}

/** The connect's stages as checklist rows; all done once it's done. */
export function stageRows(
  view: SetupView,
  ai: Pick<Ai, "status" | "stage">,
  provider: AiProvider | null,
): ChecklistRow[] {
  const at = view === "done" ? { status: "connected" as const } : ai;
  return connectStages(at, provider).map((s) => ({ id: s.stage, label: s.label, state: s.state }));
}

/** The footer under the stages: nothing can be asked yet, or the tab can close. */
export function progressNote(view: SetupView): string {
  return view === "failed"
    ? "Nothing can be asked until your AI is connected."
    : "You can close this tab. It finishes on its own, and you'll land on Chats next time.";
}

/** Past onboarding while still on /setup: the connect just finished. */
export function connectDone(phase: Phase, computer: Me["computer"]): boolean {
  return phase === "ready" && computer !== null && !isOnboarding(computer);
}

/** Card 2's title: Connect your AI while choosing, else Your AI. */
export function connectTitle(view: SetupView): string {
  return view === "form" || view === "sign-in" ? "Connect your AI" : "Your AI";
}
